import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { formatMoney } from '../../../../util/currency';
import { createSlug } from '../../../../util/urlHelpers';
import {
  IconArrowHead,
  IconTrash,
  InlineTextButton,
  NamedLink,
  PrimaryButton,
  ResponsiveImage,
} from '../../../../components';

import css from './CartListing.module.css';

/**
 * Cart row renderer.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const CartListing = props => {
  const intl = useIntl();
  const {
    row,
    selected,
    onSelect,
    onRemove,
    onCheckout,
    layoutListingImageConfig,
    hasMultipleRows,
  } = props;
  const { listing, staleReason, isStale } = row;
  const title =
    listing?.attributes?.title || intl.formatMessage({ id: 'CartPage.missingListingTitle' });
  const price = listing?.attributes?.price;
  const image = listing?.images?.[0];
  const variants = image
    ? Object.keys(image?.attributes?.variants || {}).filter(key =>
        key.startsWith(layoutListingImageConfig?.variantPrefix || 'listing-card')
      )
    : [];
  const listingId = listing?.id?.uuid;
  const titleMaybe = listingId ? (
    <NamedLink
      className={css.titleLink}
      name="ListingPage"
      params={{ id: listingId, slug: createSlug(title) }}
    >
      {title}
    </NamedLink>
  ) : (
    <span className={css.titleLink}>{title}</span>
  );

  return (
    <article className={classNames(css.root, { [css.rootSingle]: !hasMultipleRows })}>
      {hasMultipleRows ? (
        <label className={css.selection}>
          <input
            checked={selected}
            className={css.checkbox}
            disabled={isStale}
            onChange={event => onSelect(event.target.checked)}
            type="checkbox"
          />
        </label>
      ) : null}

      <div className={css.imageWrapper}>
        {image ? (
          <ResponsiveImage
            alt={title}
            image={image}
            rootClassName={css.image}
            variants={variants}
          />
        ) : null}
      </div>

      <div className={css.content}>
        <div className={css.main}>
          {titleMaybe}
          {price ? <p className={css.price}>{formatMoney(intl, price)}</p> : null}
          {isStale ? <p className={css.staleReason}>{staleReason}</p> : null}
        </div>

        <div className={css.actions}>
          <InlineTextButton
            aria-label={intl.formatMessage({ id: 'CartPage.removeItem' })}
            onClick={onRemove}
            rootClassName={css.removeButton}
            title={intl.formatMessage({ id: 'CartPage.removeItem' })}
          >
            <span className={css.removeIconWrap} aria-hidden="true">
              <IconTrash className={css.removeIcon} />
            </span>
            <span className={css.visuallyHidden}>
              <FormattedMessage id="CartPage.removeItem" />
            </span>
          </InlineTextButton>

          <PrimaryButton
            disabled={isStale}
            onClick={onCheckout}
            rootClassName={css.checkoutButton}
            type="button"
          >
            <span className={css.checkoutLabel}>
              <FormattedMessage id="CartPage.checkout" />
            </span>
            <IconArrowHead className={css.checkoutIcon} direction="right" />
          </PrimaryButton>
        </div>
      </div>
    </article>
  );
};

export default CartListing;
